import { Box, Button, IconButton, SxProps, Theme, Typography } from '@mui/material'
import React from 'react'

import { ExpandLess } from '@mui/icons-material'

export type MessageType = 'warning' | 'error' | 'info' | 'success'

interface InfoBoxProps {
  content: string
  messageType: MessageType
  sx?: SxProps<Theme>
  actions?: React.ReactNode
  isCollapsed?: boolean // New prop for collapsing functionality
  hiddenCount?: number // Number of hidden items for this type
  onToggleCollapse?: () => void // Handler for toggling collapse
  hasCollapseButton?: boolean // Whether to display the collapse button
}

export const InfoBox: React.FC<InfoBoxProps> = ({
  content,
  messageType,
  sx,
  actions,
  isCollapsed = false,
  hiddenCount = 0,
  onToggleCollapse,
  hasCollapseButton = false,
}) => {
  const palettes = {
    warning: {
      border: 'rgba(243, 172, 0, 0.22)',
      background: 'rgba(255, 243, 219, 0.88)',
      accent: '#D39A12',
      text: '#203864',
    },
    error: {
      border: 'rgba(220, 53, 69, 0.18)',
      background: 'rgba(252, 236, 239, 0.9)',
      accent: '#D94B5A',
      text: '#203864',
    },
    info: {
      border: 'rgba(105, 141, 172, 0.18)',
      background: 'rgba(232, 242, 251, 0.92)',
      accent: '#698DAC',
      text: '#203864',
    },
    success: {
      border: 'rgba(88, 140, 99, 0.18)',
      background: 'rgba(235, 246, 238, 0.92)',
      accent: '#5D9167',
      text: '#203864',
    },
  } as const

  const palette = palettes[messageType]

  return (
    <Box
      sx={{
        px: 1.6,
        py: 1,
        color: palette.text,
        border: `1px solid ${palette.border}`,
        borderRadius: 2.5,
        margin: (theme) => theme.spacing(0.5, 0),
        fontSize: (theme) => theme.typography.body2.fontSize,
        background: palette.background,
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        gap: 0.9,
        ...sx,
      }}
    >
      <Box
        sx={{
          width: 8,
          alignSelf: 'stretch',
          flexShrink: 0,
          borderRadius: '999px',
          backgroundColor: palette.accent,
        }}
      />
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: palette.text,
          flex: 1,
          marginRight: 0.5,
          fontSize: { xs: 13.5, sm: 14 },
          lineHeight: 1.35,
          pt: { xs: 0.05, sm: 0 },
        }}
      >
        {content}
      </Typography>
      {actions}

      {hiddenCount > 0 && onToggleCollapse && (
        <Button
          onClick={(e) => {
            e.stopPropagation()
            onToggleCollapse()
          }}
          size="small"
          sx={{
            textTransform: 'none',
            fontSize: '0.75rem',
            padding: 0.6,
            minWidth: 50,
            m: 0,
            color: palette.text,
            fontWeight: 700,
            backgroundColor: 'rgba(255,255,255,0.72)',
            border: `1px solid ${palette.border}`,
            borderRadius: '999px',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.92)',
            },
          }}
        >
          {isCollapsed ? `+ ${hiddenCount} autre(s)` : 'Collapse'}
        </Button>
      )}
      {hiddenCount <= 0 && onToggleCollapse && hasCollapseButton && (
        <IconButton
          size="small"
          onClick={onToggleCollapse}
          sx={{ height: 24, width: 24, color: palette.accent, backgroundColor: 'rgba(255,255,255,0.56)' }}
        >
          <ExpandLess />
        </IconButton>
      )}
    </Box>
  )
}
